import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { estToday, scoreColor, SCALE } from "@/lib/grades";
import { useMetrics } from "@/context/MetricsContext";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Sparkles, Pill, Stethoscope } from "lucide-react";

// patientId: undefined => self (patient). string => admin logging for a patient.
export function DailyLogCard({ patientId, existing, onSaved, patientName }) {
  const metrics = useMetrics();
  const [values, setValues] = useState(() => {
    const init = {};
    metrics.forEach((m) => (init[m.key] = existing?.metrics?.[m.key] ?? null));
    return init;
  });
  const [note, setNote] = useState(existing?.note || "");
  const [medsTaken, setMedsTaken] = useState(existing?.meds_taken || false);
  const [doctorVisit, setDoctorVisit] = useState(existing?.doctor_visit || false);
  const [saving, setSaving] = useState(false);
  const today = estToday();

  const setVal = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    const filled = {};
    metrics.forEach((m) => {
      if (values[m.key] != null) filled[m.key] = values[m.key];
    });
    if (Object.keys(filled).length < metrics.length) {
      toast.error("Please score every metric before saving");
      return;
    }
    setSaving(true);
    try {
      const body = { metrics: filled, note, meds_taken: medsTaken, doctor_visit: doctorVisit };
      if (patientId) body.patient_id = patientId;
      await api.post("/entries", body);
      toast.success(existing ? "Entry updated" : "Entry saved for today");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 sm:p-8 rounded-2xl border-border/70 shadow-sm animate-fade-up" data-testid="daily-log-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-5 w-5 text-primary" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{today} &middot; Eastern</span>
      </div>
      <h2 className="text-2xl sm:text-3xl font-serif mb-6">
        {patientId ? `Log today for ${patientName || "this patient"}` : "How was your day?"}
      </h2>

      <div className="space-y-6 mb-6">
        {metrics.map((m) => (
          <div key={m.key} data-testid={`metric-${m.key}`}>
            <div className="flex items-baseline justify-between mb-2.5 flex-wrap gap-1">
              <div>
                <span className="font-semibold">{m.label}</span>
                <span className="text-xs text-muted-foreground ml-2">{m.description}</span>
              </div>
              <span
                className="font-mono text-sm font-bold"
                style={{ color: values[m.key] != null ? scoreColor(values[m.key]) : "hsl(var(--muted-foreground))" }}
                data-testid={`metric-${m.key}-value`}
              >
                {values[m.key] != null ? `${values[m.key]} / 10` : "—"}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SCALE.map((n) => {
                const active = values[m.key] === n;
                return (
                  <button
                    key={n}
                    data-testid={`${m.key}-score-${n}`}
                    onClick={() => setVal(m.key, n)}
                    className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg text-sm font-mono font-semibold transition-all duration-150 border-2"
                    style={{
                      borderColor: active ? scoreColor(n) : "transparent",
                      backgroundColor: active ? scoreColor(n) : "hsl(var(--secondary))",
                      color: active ? "#fff" : "hsl(var(--foreground))",
                      transform: active ? "translateY(-2px)" : "none",
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Textarea
        data-testid="daily-note-input"
        placeholder="Anything special that happened today? (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        className="resize-none rounded-xl mb-4"
      />

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <label htmlFor="meds-taken"
          className="flex items-center gap-3 rounded-xl bg-secondary px-4 py-3 cursor-pointer select-none">
          <Checkbox id="meds-taken" data-testid="meds-taken-checkbox" checked={medsTaken}
            onCheckedChange={(v) => setMedsTaken(!!v)} />
          <Pill className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Took prescribed meds</span>
        </label>
        <label htmlFor="doctor-visit"
          className="flex items-center gap-3 rounded-xl bg-secondary px-4 py-3 cursor-pointer select-none">
          <Checkbox id="doctor-visit" data-testid="doctor-visit-checkbox" checked={doctorVisit}
            onCheckedChange={(v) => setDoctorVisit(!!v)} />
          <Stethoscope className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Had a doctor's appointment</span>
        </label>
      </div>

      <div className="flex justify-end">
        <Button data-testid="daily-submit-button" onClick={submit} disabled={saving} className="rounded-xl px-8">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {existing ? "Update entry" : "Save entry"}
        </Button>
      </div>
    </Card>
  );
}
